import { useEffect, useRef } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { Dropdown, type DropdownItem } from "@/components/sidebar/Dropdown";
import {
  createBlock,
  insertFile,
  insertImageFile,
  moveSelectionToEnd,
  type CreateBlockType,
} from "@/components/tiptap/utils/editorActions";
import { getIsIOS } from "@/utils/getIsIOS";
import { twMerge } from "@/utils/twMerge";
import { VALID_IMAGE_TYPES } from "@/utils/validateImageFile";

import type { DocumentModel } from "@/models/DocumentModel";
import type { Editor } from "@tiptap/core";
import type { ChangeEvent } from "react";

/**
 * Editors the user has placed the caret in. Kept per editor (not per button
 * mount) so hiding and re-showing the button doesn't forget a cursor the user
 * placed before.
 */
const caretPlacedEditors = new WeakSet<Editor>();

/** Records the first time the user places the caret in `editor`. */
function useTrackCaretPlaced(editor: Editor | null) {
  useEffect(() => {
    if (!editor) return;
    const markCaretPlaced = () => {
      caretPlacedEditors.add(editor);
    };
    if (editor.isFocused) markCaretPlaced();
    editor.on("focus", markCaretPlaced);
    return () => {
      editor.off("focus", markCaretPlaced);
    };
  }, [editor]);
}

/**
 * The mobile "Add block" menu: images, photos and files, plus the blocks the
 * mobile app offers. Mirrors the app's DocFooter button, so blocks can be added
 * without opening the keyboard.
 */
export const AddBlockButton = observer(function AddBlockButton({
  doc,
  className,
}: {
  doc: DocumentModel;
  className?: string;
}) {
  const t = useTranslations("doc.addBlock");
  const { tiptap } = doc.editor;
  useTrackCaretPlaced(tiptap ?? null);
  // The inputs live outside the menu: it unmounts on item click, before the
  // picker reports the chosen file.
  const photoLibraryInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!tiptap || !doc.editable) {
    return null;
  }

  const prepareInsert = () => {
    if (!caretPlacedEditors.has(tiptap)) moveSelectionToEnd(tiptap);
  };

  const handleFile = (insert: (file: File) => void) => (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so picking the same file again still fires `change`.
    event.target.value = "";
    if (!file) return;
    prepareInsert();
    insert(file);
  };

  const addBlock = (type: CreateBlockType) => {
    prepareInsert();
    createBlock(doc, type);
  };

  const takePhotoItem: DropdownItem = {
    title: t("takePhoto"),
    icon: <Icon name="chat-icons/Camera" width={24} height={24} />,
    onClick: () => cameraInputRef.current?.click(),
  };

  // iOS answers a file input with its own Photo Library / Choose File sheet, so
  // a single item there avoids offering the same choice twice.
  const pickerItems: DropdownItem[] = getIsIOS()
    ? [
        takePhotoItem,
        {
          title: t("photoOrFile"),
          icon: <Icon name="Folder" width={24} height={24} />,
          onClick: () => fileInputRef.current?.click(),
        },
      ]
    : [
        {
          title: t("photoLibrary"),
          icon: <Icon name="Image" width={24} height={24} />,
          onClick: () => photoLibraryInputRef.current?.click(),
        },
        takePhotoItem,
        {
          title: t("chooseFile"),
          icon: <Icon name="Folder" width={24} height={24} />,
          onClick: () => fileInputRef.current?.click(),
        },
      ];

  const items: DropdownItem[] = [
    ...pickerItems,
    { type: "separator" },
    {
      title: t("code"),
      icon: <Icon name="block-format/Code" width={24} height={24} />,
      onClick: () => addBlock("code"),
    },
    {
      title: t("table"),
      icon: <Icon name="block-format/Table" width={24} height={24} />,
      onClick: () => addBlock("table"),
    },
    {
      title: t("separationLine"),
      icon: <Icon name="block-format/Line" width={24} height={24} />,
      onClick: () => addBlock("horizontalRule"),
    },
  ];

  return (
    <>
      <Dropdown
        items={items}
        anchor={{ to: "top start", gap: 8 }}
        toggleButtonLabel={t("label")}
        className={twMerge(
          "flex shrink-0 items-center justify-center w-14.5 h-13.5 rounded-[20px] bg-document-bg chat-input-surface",
          className,
        )}
        toggleButtonComponent={<Icon name="Add" width={32} height={32} />}
      />
      <input
        ref={photoLibraryInputRef}
        className="hidden"
        type="file"
        accept={VALID_IMAGE_TYPES.join(",")}
        onChange={handleFile((file) => insertImageFile(doc, file))}
      />
      <input
        ref={cameraInputRef}
        className="hidden"
        type="file"
        accept="image/*"
        capture="environment"
        // Selected so its Recognize (OCR) button shows right away.
        onChange={handleFile((file) => insertImageFile(doc, file, undefined, { select: true }))}
      />
      <input
        ref={fileInputRef}
        className="hidden"
        type="file"
        onChange={handleFile((file) => insertFile(doc, file))}
      />
    </>
  );
});
